import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, MessageSquare, Lightbulb, AlertTriangle, AlertOctagon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'question', labelKey: 'help.contact.catQuestion', labelDefault: 'Duda', icon: MessageSquare, color: 'text-blue-600 bg-blue-50' },
  { key: 'suggestion', labelKey: 'help.contact.catSuggestion', labelDefault: 'Sugerencia', icon: Lightbulb, color: 'text-amber-600 bg-amber-50' },
  { key: 'incident', labelKey: 'help.contact.catIncident', labelDefault: 'Incidencia', icon: AlertTriangle, color: 'text-orange-600 bg-orange-50' },
  { key: 'complaint', labelKey: 'help.contact.catComplaint', labelDefault: 'Reclamación', icon: AlertOctagon, color: 'text-rose-600 bg-rose-50' },
];

const HelpContactForm = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    category: 'question',
    subject: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(t('help.contact.fillAll', 'Por favor rellena todos los campos'));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/help/contact`, form, { withCredentials: true });
      toast.success(t('help.contact.sent', '¡Mensaje enviado! Te responderemos pronto.'));
      setSent(true);
      setForm({ ...form, subject: '', message: '' });
    } catch (err) {
      toast.error(t('help.contact.error', 'Error al enviar. Inténtalo de nuevo.'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="contact-sent-view">
        <div className="w-16 h-16 rounded-full bg-emerald-500 mx-auto flex items-center justify-center mb-4">
          <Send className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('help.contact.thanksTitle', '¡Mensaje recibido!')}</h3>
        <p className="text-slate-600 mb-6">{t('help.contact.thanksDesc', 'Te hemos enviado un email de confirmación. Responderemos en menos de 24 horas laborables.')}</p>
        <Button onClick={() => setSent(false)} variant="outline" data-testid="contact-send-another">
          {t('help.contact.sendAnother', 'Enviar otro mensaje')}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="help-contact-form">
      {/* Category */}
      <div>
        <Label className="mb-2 block">{t('help.contact.categoryLabel', 'Tipo de consulta')}</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {CATEGORIES.map(c => {
            const Icon = c.icon;
            const active = form.category === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setForm({ ...form, category: c.key })}
                data-testid={`contact-cat-${c.key}`}
                className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  active
                    ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className={`p-2 rounded-full ${c.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-xs font-medium">{t(c.labelKey, c.labelDefault)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contact-name">{t('help.contact.name', 'Nombre')}</Label>
          <Input id="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name-input" />
        </div>
        <div>
          <Label htmlFor="contact-email">{t('help.contact.email', 'Email')}</Label>
          <Input id="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email-input" />
        </div>
      </div>

      <div>
        <Label htmlFor="contact-subject">{t('help.contact.subject', 'Asunto')}</Label>
        <Input id="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="contact-subject-input" />
      </div>

      <div>
        <Label htmlFor="contact-message">{t('help.contact.message', 'Mensaje')}</Label>
        <Textarea id="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} required data-testid="contact-message-input" />
        <p className="text-xs text-slate-500 mt-1">{t('help.contact.privacy', 'Tus datos se usan solo para responder a tu consulta.')}</p>
      </div>

      <Button type="submit" disabled={sending} className="w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white" data-testid="contact-submit-btn">
        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        {t('help.contact.submit', 'Enviar mensaje')}
      </Button>
    </form>
  );
};

export default HelpContactForm;
